import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CloseIcon } from "@/assets/icons";
import Modal from "@/components/ui/modal";
import { WidgetType } from "@/types/widget";

import FormWidgetConfiguration from "./FormWidgetConfiguration";
import WidgetConfigurationTab from "./WidgetConfigurationTab";

export interface WidgetFormData {
  chart_type: WidgetType;
  label: string;
  dataset_id: string;
  dimension: string;
  measure: string;
  aggregation: string;
  order_by: string;
  dimension_values_shown: number;
}

interface WidgetConfigurationProps {
  open: boolean;
  onClose: () => void;
  // True when the modal is opened to configure an existing widget; locks the
  // chart-type tabs so the user cannot switch chart types mid-edit.
  isEditing: boolean;
}

const WidgetConfiguration = ({
  open,
  onClose,
  isEditing
}: WidgetConfigurationProps) => {
  const method = useForm({
    defaultValues: {
      chart_type: WidgetType.BAR_V,
      label: "",
      dataset_id: "",
      dimension: "",
      measure: "",
      aggregation: "",
      order_by: "",
      dimension_values_shown: 10
    }
  });

  const { watch, setValue, reset } = method;

  const watchChartType = watch("chart_type");

  // Reset stale form state when the modal is reopened so the previous session's
  // partial input does not leak into the next "Add" or "Edit" flow.
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      shouldCloseModalOnClickOutside={false}
    >
      <div className="w-[80vw] h-[90vh] bg-white p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-txtHeading">
            Widget Configuration
          </h2>
          <button onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="flex gap-2 mt-4 h-[calc(90vh-80px)]">
          <FormProvider {...method}>
            <FormWidgetConfiguration widgetType={watchChartType} />
            <WidgetConfigurationTab
              widgetType={watchChartType}
              setWidgetType={(type) => {
                reset();
                setValue("chart_type", type);
              }}
              isEditing={isEditing}
            />
          </FormProvider>
        </div>
      </div>
    </Modal>
  );
};

export default WidgetConfiguration;
